import React, { useState, useEffect } from 'react';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import useSessionManager from '@afosto/auth-react/useSessionManager';
import { useSnackbar } from 'notistack';
import { FormattedMessage } from 'react-intl';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import Divider from '@material-ui/core/Divider';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Typography from '@material-ui/core/Typography';
import TwofaSetupForm from '../../../components/TwofaSetupForm';
import FormDrawer from '@afosto/app-components-react/FormDrawer';
import Dialog from '@afosto/app-components-react/Dialog';
import SkeletonBody from '@afosto/components-react/SkeletonBody';
import { twofa as twofaApi } from '@afosto/iam/api/Twofa';
import { users as usersApi } from '@afosto/iam/api/Users';
import translations from './translations';

const ProfileSecurity = () => {
  const [hasPermissions] = usePermissions(['iam:2fa:read', 'iam:2fa:write']);
  const intl = useIntl();
  const [showAddAuthenticatorApp, setShowAddAuthenticatorApp] = useState(false);
  const [currentStep, setCurrentStep] = useState('backup');
  const [isFetchingSecret, setIsFetchingSecret] = useState(false);
  const [activeSecret, setActiveSecret] = useState(undefined);
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const sessionManager = useSessionManager();
  const { user: { email = null } = {} } = sessionManager || {};

  const handleClose = () => {
    setShowAddAuthenticatorApp(false);
    setCurrentStep('backup');
  };

  const requestPasswordReset = async () => {
    try {
      const response = await usersApi.requestToken({
        email,
        type: 'reset',
      });
      const { data = {} } = response;

      enqueueSnackbar(
        intl.formatMessage(translations.sections.password.feedback.resetPasswordEmailSent),
        {
          variant: 'success',
        },
      );
      return data;
    } catch (err) {
      if (err?.response?.data?.code === 400) {
        return enqueueSnackbar(
          intl.formatMessage(translations.sections.password.feedback.resetPasswordEmailAlreadySent),
          {
            variant: 'error',
          },
        );
      }

      enqueueSnackbar(intl.formatMessage(translations.sections.password.feedback.unknownError), {
        variant: 'error',
      });

      return Promise.reject(err);
    }
  };

  const disable2fa = async () => {
    try {
      const response = await twofaApi.deleteSecret('login');
      setActiveSecret(undefined);
      setShowDisableConfirm(false);
      return response;
    } catch (err) {
      return Promise.reject(err);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const fetchSecret = async () => {
      setIsFetchingSecret(true);
      try {
        const response = await twofaApi.getSecret('login');
        const { data = {} } = response;
        if (isMounted) {
          setActiveSecret(data);
        }
      } catch (err) {
        if (isMounted) {
          setActiveSecret(null);
        }
      } finally {
        if (isMounted) {
          setIsFetchingSecret(false);
        }
      }
    };

    fetchSecret();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <>
      <Card style={{ marginBottom: 16 }}>
        <CardHeader
          title={intl.formatMessage(translations.sections.password.title)}
          subheader={intl.formatMessage(translations.sections.password.description)}
          titleTypographyProps={{ variant: 'h6' }}
          subheaderTypographyProps={{ variant: 'body2' }}
          action={
            <Button variant="outlined" color="default" onClick={requestPasswordReset}>
              <FormattedMessage {...translations.sections.password.actions.edit} />
            </Button>
          }
        />
      </Card>
      {hasPermissions() && (
        <Card>
          <CardHeader
            title={intl.formatMessage(translations.sections.twofa.title)}
            subheader={intl.formatMessage(translations.sections.twofa.description)}
            titleTypographyProps={{ variant: 'h6' }}
            subheaderTypographyProps={{ variant: 'body2' }}
          />
          <Divider />
          {isFetchingSecret ? (
            <CardContent>
              <SkeletonBody lines={2} />
            </CardContent>
          ) : (
            <CardHeader
              title={intl.formatMessage(translations.sections.twofa.authenticatorApp.title)}
              subheader={
                activeSecret
                  ? intl.formatMessage(translations.sections.twofa.authenticatorApp.activeSince, {
                      date: intl.formatDate(activeSecret.created_at),
                    })
                  : intl.formatMessage(translations.sections.twofa.authenticatorApp.description)
              }
              titleTypographyProps={{ variant: 'subtitle1' }}
              subheaderTypographyProps={{ variant: 'body2' }}
              action={
                activeSecret ? (
                  <Button
                    variant="outlined"
                    color="default"
                    onClick={() => setShowDisableConfirm(true)}
                  >
                    <FormattedMessage {...translations.sections.twofa.actions.disable} />
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    color="default"
                    onClick={() => setShowAddAuthenticatorApp(true)}
                  >
                    <FormattedMessage {...translations.sections.twofa.actions.configure} />
                  </Button>
                )
              }
            />
          )}
          {activeSecret?.backups && (
            <CardContent>
              <Typography variant="subtitle2">
                <FormattedMessage {...translations.sections.twofa.authenticatorApp.backups.title} />
              </Typography>
              <List dense disablePadding>
                {activeSecret.backups.map(item => (
                  <ListItem disableGutters key={item.channel}>
                    <ListItemText
                      primary={intl.formatMessage(
                        translations.sections.twofa.authenticatorApp.backups.channels[item.channel],
                      )}
                      primaryTypographyProps={{ variant: 'overline', color: 'textSecondary' }}
                      secondary={item.address}
                      secondaryTypographyProps={{ variant: 'body2', color: 'textPrimary' }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          )}
        </Card>
      )}

      <FormDrawer
        isOpen={showAddAuthenticatorApp}
        title={intl.formatMessage(translations.form.steps[currentStep].title)}
        onClose={handleClose}
        drawerWidth="440px"
      >
        <TwofaSetupForm
          setCurrentStep={setCurrentStep}
          currentStep={currentStep}
          handleClose={handleClose}
          setActiveSecret={setActiveSecret}
        />
      </FormDrawer>

      <Dialog open={showDisableConfirm} onClose={() => setShowDisableConfirm(false)} maxWidth="xs">
        <DialogTitle>
          <FormattedMessage {...translations.sections.twofa.actions.deleteDialog.title} />
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <FormattedMessage {...translations.sections.twofa.actions.deleteDialog.text} />
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDisableConfirm(false)} variant="outlined" color="default">
            <FormattedMessage {...translations.sections.twofa.actions.deleteDialog.cancel} />
          </Button>
          <Button onClick={disable2fa} color="primary" variant="contained" autoFocus>
            <FormattedMessage {...translations.sections.twofa.actions.deleteDialog.disable} />
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProfileSecurity;
